/**
 * Created: 2026-08-05
 * Initial commit: Extract shared dashboard date-range picker
 *
 * Shortcut buttons + MUI DateRangePicker used by dashboard pages to pick the
 * date range charts are queried over. Extracted from DashboardPage so the
 * site and admin dashboards can each own their own range independently.
 */

import { Box, Button, ButtonGroup } from '@mui/material'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import type { DateRange } from '@mui/x-date-pickers-pro/models'
import { format, startOfDay, startOfMonth, subDays } from 'date-fns'
import { useMemo, useState } from 'react'

const shortcutButtons: { label: string; getRange: () => DateRange<Date> }[] = [
  {
    label: '近7天',
    getRange: () => {
      const end = new Date()
      return [subDays(end, 6), end]
    },
  },
  {
    label: '本月',
    getRange: () => [startOfMonth(new Date()), new Date()],
  },
  {
    label: '近30天',
    getRange: () => {
      const end = new Date()
      return [subDays(end, 29), end]
    },
  },
]

export function defaultRange(): DateRange<Date> {
  const end = new Date()
  return [subDays(end, 6), end]
}

/** Returns the index of the shortcut whose range matches [start, end], or -1 if none. */
function activeShortcutIndex(range: DateRange<Date>): number {
  const [start, end] = range
  if (!start || !end) return -1
  const startStr = format(startOfDay(start), 'yyyy-MM-dd')
  const endStr = format(startOfDay(end), 'yyyy-MM-dd')
  return shortcutButtons.findIndex((s) => {
    const [rs, re] = s.getRange()
    if (!rs || !re) return false
    return (
      format(startOfDay(rs), 'yyyy-MM-dd') === startStr &&
      format(startOfDay(re), 'yyyy-MM-dd') === endStr
    )
  })
}

interface DashboardRangePickerProps {
  range: DateRange<Date>
  onRangeChange: (range: DateRange<Date>) => void
}

/**
 * Shortcut buttons (近7天/本月/近30天) plus a manual MUI DateRangePicker.
 * `onRangeChange` only fires for a fully valid range; the picker's
 * in-progress typing is tracked locally so partial input doesn't trigger
 * queries with an invalid range.
 */
export default function DashboardRangePicker({
  range,
  onRangeChange,
}: DashboardRangePickerProps) {
  const [displayRange, setDisplayRange] = useState<DateRange<Date>>(range)
  const maxDate = useMemo(() => new Date(), [])

  const handleRangeChange = (newRange: DateRange<Date>) => {
    setDisplayRange(newRange)
    const [start, end] = newRange
    if (
      start &&
      end &&
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      start.getFullYear() >= 2000 &&
      end.getFullYear() >= 2000
    ) {
      onRangeChange(newRange)
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <ButtonGroup size="small" variant="outlined">
        {shortcutButtons.map((s, i) => {
          const active = activeShortcutIndex(displayRange) === i
          return (
            <Button
              key={s.label}
              variant={active ? 'contained' : 'outlined'}
              onClick={() => {
                const r = s.getRange()
                setDisplayRange(r)
                onRangeChange(r)
              }}
            >
              {s.label}
            </Button>
          )
        })}
      </ButtonGroup>
      <DateRangePicker
        value={displayRange}
        onChange={handleRangeChange}
        format="yyyy-MM-dd"
        maxDate={maxDate}
        localeText={{ start: '开始日期', end: '结束日期' }}
        slotProps={{
          textField: {
            size: 'small',
            slotProps: { inputLabel: { shrink: true } },
          },
        }}
      />
    </Box>
  )
}
